import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type {
  DateSelectionMode,
  DatePickerValue,
  DatePickerSingleValue,
  DatePickerRangeValue,
  DatePickerMultipleValue,
  DateSelectEventDetail,
  DateClearEventDetail,
} from '../types.js';
import { emit } from '../utils/dom.utils.js';

// ─────────────────────────────────────────────────────────────────────────────
// SelectionHost
// ─────────────────────────────────────────────────────────────────────────────

export interface SelectionHost extends ReactiveControllerHost {
  selectionMode:  DateSelectionMode;
  minDate:        Date | null;
  maxDate:        Date | null;
  disabledDates:  Date[];
  disabledDays:   number[];
}

// ─────────────────────────────────────────────────────────────────────────────
// SelectionController
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages selection state for single / range / multiple modes.
 * Does NOT render anything — calls host.requestUpdate() after mutations.
 */
export class SelectionController implements ReactiveController {
  readonly host: SelectionHost;

  // Current selection value — single source of truth
  value: DatePickerValue = null;

  // Range hover preview (used in range mode while first date is chosen)
  private _hoverDate: Date | null = null;

  constructor(host: SelectionHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void { /* no-op */ }
  hostDisconnected(): void { /* no-op */ }

  // ── Sync from external property ──────────────────────────────────

  syncFromProp(val: DatePickerValue): void {
    this.value = val;
  }

  // ── Query helpers (used by host render) ──────────────────────────

  isSelected(date: Date): boolean {
    const mode = this.host.selectionMode;
    if (mode === 'single') {
      return this.value !== null && this._isSameDay(date, this.value as Date);
    }
    if (mode === 'multiple') {
      return (this.value as DatePickerMultipleValue ?? []).some(d => this._isSameDay(d, date));
    }
    if (mode === 'range') {
      const r = this.value as DatePickerRangeValue;
      if (!r) return false;
      return this._isSameDay(date, r[0]) || (r[1] !== null && this._isSameDay(date, r[1]));
    }
    return false;
  }

  isRangeStart(date: Date): boolean {
    if (this.host.selectionMode !== 'range') return false;
    const r = this.value as DatePickerRangeValue;
    return !!r && this._isSameDay(date, r[0]);
  }

  isRangeEnd(date: Date): boolean {
    if (this.host.selectionMode !== 'range') return false;
    const r = this.value as DatePickerRangeValue;
    return !!r && r[1] !== null && this._isSameDay(date, r[1]);
  }

  isInRange(date: Date): boolean {
    if (this.host.selectionMode !== 'range') return false;
    const r = this.value as DatePickerRangeValue;
    if (!r || r[1] === null) return false;
    const t = date.getTime();
    return t > r[0].getTime() && t < r[1].getTime();
  }

  isHoverRange(date: Date): boolean {
    if (this.host.selectionMode !== 'range') return false;
    const r = this.value as DatePickerRangeValue;
    // Only active when first date is set but second is not
    if (!r || r[1] !== null) return false;
    if (!this._hoverDate) return false;
    const start = r[0].getTime();
    const end   = this._hoverDate.getTime();
    const t     = date.getTime();
    if (start < end) return t > start && t < end;
    if (start > end) return t < start && t > end;
    return false;
  }

  hasValue(): boolean {
    const mode = this.host.selectionMode;
    if (mode === 'single') return this.value !== null;
    if (mode === 'multiple') return Array.isArray(this.value) && (this.value as Date[]).length > 0;
    if (mode === 'range') return this.value !== null;
    return false;
  }

  // ── Mutations ────────────────────────────────────────────────────

  select(date: Date, originalEvent?: Event): void {
    if (this._isDateDisabled(date)) return;

    const prev = this.value;
    const mode = this.host.selectionMode;

    if (mode === 'single') {
      this.value = date as DatePickerSingleValue;
    } else if (mode === 'multiple') {
      const arr = [...((this.value as DatePickerMultipleValue) ?? [])];
      const idx = arr.findIndex(d => this._isSameDay(d, date));
      if (idx >= 0) arr.splice(idx, 1);
      else          arr.push(date);
      this.value = arr as DatePickerMultipleValue;
    } else if (mode === 'range') {
      const r = this.value as DatePickerRangeValue;
      if (!r || r[1] !== null) {
        // Start new range
        this.value = [date, null];
      } else {
        // Complete range — ensure start <= end
        const [start] = r;
        if (date.getTime() >= start.getTime()) {
          this.value = [start, date];
        } else {
          this.value = [date, start];
        }
        this._hoverDate = null;
      }
    }

    const detail: DateSelectEventDetail = {
      value:         this.value,
      selectionMode: this.host.selectionMode,
      originalEvent,
    };
    emit(this.host as unknown as Element, 'uwc-date-select', detail);
    this.host.requestUpdate();
    return void prev;
  }

  handleHover(date: Date | null): void {
    if (this.host.selectionMode !== 'range') return;
    this._hoverDate = date;
    this.host.requestUpdate();
  }

  clear(): void {
    const previousValue = this.value;
    this.value = this.host.selectionMode === 'multiple' ? [] : null;
    this._hoverDate = null;

    const detail: DateClearEventDetail = { previousValue };
    emit(this.host as unknown as Element, 'uwc-date-clear', detail);
    this.host.requestUpdate();
  }

  // ── Form value ────────────────────────────────────────────────────

  getFormValue(): string | null {
    const mode = this.host.selectionMode;
    if (mode === 'single') {
      return this.value ? (this.value as Date).toISOString() : null;
    }
    if (mode === 'multiple') {
      const arr = this.value as DatePickerMultipleValue ?? [];
      return arr.length ? arr.map(d => d.toISOString()).join(',') : null;
    }
    if (mode === 'range') {
      const r = this.value as DatePickerRangeValue;
      if (!r) return null;
      return r[1] ? `${r[0].toISOString()}/${r[1].toISOString()}` : r[0].toISOString();
    }
    return null;
  }

  // ── Helpers ──────────────────────────────────────────────────────

  private _isSameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth()    === b.getMonth()
        && a.getDate()     === b.getDate();
  }

  private _isDateDisabled(date: Date): boolean {
    const { minDate, maxDate, disabledDates, disabledDays } = this.host;
    const startOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (minDate) {
      const minStart = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
      if (startOfDay < minStart) return true;
    }
    if (maxDate) {
      const maxStart = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
      if (startOfDay > maxStart) return true;
    }
    if (disabledDays.includes(date.getDay())) return true;
    if (disabledDates.some(d => this._isSameDay(d, date))) return true;
    return false;
  }
}
